import { toast } from 'sonner';

export class ApiError extends Error {
  readonly status: number;
  readonly bodyText: string;

  constructor(status: number, bodyText: string) {
    super(`API ${status}: ${bodyText || '(empty)'}`);
    this.name = 'ApiError';
    this.status = status;
    this.bodyText = bodyText;
  }
}

export async function ensureOk(res: Response): Promise<Response> {
  if (res.ok) return res;
  // `304` is not `res.ok`, and it carries no body — there is nothing for
  // `parseJson` to read, so without this branch it would surface as a
  // misleading "expected JSON" parse failure. `authedFetch` already re-issues a
  // conditional request uncached, so getting here means that retry also came
  // back `304`. Fail with a named cause; the text is diagnostic, never shown.
  if (res.status === 304) {
    throw new ApiError(304, 'not modified: no cached body to pair with the validator');
  }
  let text = '';
  try {
    text = await res.text();
  } catch {
    text = '';
  }
  throw new ApiError(res.status, text);
}

type ErrorSurface = { kind: 'toast'; message: string } | { kind: 'silent' } | { kind: 'inline' };

export function describeApiError(error: unknown): ErrorSurface {
  if (!(error instanceof ApiError)) {
    return { kind: 'toast', message: 'Something went wrong on our end.' };
  }
  switch (error.status) {
    // Only reachable when ETag revalidation broke down (see `ensureOk`). The
    // user can do nothing about it, so say nothing specific.
    case 304:
      return { kind: 'toast', message: 'Something went wrong on our end.' };
    case 400:
      return { kind: 'toast', message: error.bodyText || 'Invalid request.' };
    case 401:
      return { kind: 'silent' };
    case 403:
      return { kind: 'toast', message: "You don't have permission for that." };
    case 404:
      return { kind: 'inline' };
    case 409:
      return { kind: 'toast', message: error.bodyText || 'Conflict — that change was rejected.' };
    case 429:
      return {
        kind: 'toast',
        message: 'Slow down — too many requests. Try again in a moment.',
      };
    default:
      if (error.status >= 500) {
        return { kind: 'toast', message: 'Something went wrong on our end.' };
      }
      return { kind: 'toast', message: error.bodyText || `Request failed (${error.status}).` };
  }
}

export function surfaceApiError(error: unknown): void {
  const surface = describeApiError(error);
  if (surface.kind === 'toast') {
    toast.error(surface.message);
  }
}
